import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import {
    Alert,
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormGroup,
    Checkbox,
    Button,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { createWarrantyOrderTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { CONSTANTS } from 'config/constants';
import { Order } from 'models/order';

const { WARRANTY_TYPES } = CONSTANTS;

type CreateWarrantyOrderDialogProps = {
    open: boolean;
    onDialogClose: () => void;
    orderDbId: string;
    orderId: string;
    onCreateWarrantyOrder: (order: Order) => void;
    isCreatedByLab: boolean;
    isDentist: boolean;
};
const CreateWarrantyOrderDialog: FC<CreateWarrantyOrderDialogProps> = ({
    open,
    onDialogClose,
    orderDbId,
    orderId,
    onCreateWarrantyOrder,
    isCreatedByLab,
    isDentist,
}: CreateWarrantyOrderDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [warrantyType, setWarrantyType] = useState('NOT_CEMENTED');
    const [orderSection, setOrderSection] = useState({
        patient: true,
        construction: true,
        comments: false,
        files: false,
    });

    useEffect(() => {
        if (open) {
            setOrderSection({ ...orderSection, construction: !(isDentist && isCreatedByLab) });
        }
    }, [open]);

    const { showBoundary } = useDenthubErrorBoundary();
    const [createWarrantyOrder] = useMutation(createWarrantyOrderTemplate, {
        onCompleted: ({ createWarrantyOrder }) => {
            onDialogClose();
            onCreateWarrantyOrder(createWarrantyOrder);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setWarrantyType((event.target as HTMLInputElement).value);
    };

    const handleSectionChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOrderSection({
            ...orderSection,
            [event.target.name]: event.target.checked,
        });
    };

    const handleCreateWarranty = async () => {
        await createWarrantyOrder({
            variables: {
                orderId: orderDbId,
                warrantyType: warrantyType,
                orderSection: orderSection,
            },
        });
    };

    return (
        <DHDialog
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={onDialogClose}
            title={translate('orders:WARRANTY_ORDER_FOR') + ' ' + orderId}
            actions={
                <>
                    <Button
                        variant="contained"
                        onClick={handleCreateWarranty}
                        data-test="create-warranty-order-dialog.create-warranty-order-btn"
                    >
                        {translate('orders:CREATE_WARRANTY_ORDER')}
                    </Button>
                </>
            }
        >
            <Box>
                <Box>
                    <Typography variant="h4">{translate('orders:WARRANTY_TYPE')}</Typography>
                </Box>
                <FormControl component="fieldset">
                    <RadioGroup value={warrantyType} onChange={handleChange}>
                        {WARRANTY_TYPES.map((elem: string) => (
                            <FormControlLabel
                                key={elem}
                                value={elem}
                                control={<Radio color="secondary" />}
                                label={translate(`orders:${elem}`)}
                                data-test={`create-warranty-order-dialog.warranty-type.${elem}`}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Box>
            <Box mt={2}>
                <Box mb={1}>
                    <Typography variant="h4">{translate('orders:SECTION_TO_BE_COPIED')}</Typography>
                </Box>
                <Box>
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translate('orders:WARRANTY_ORDER_SECTION_INFO')}
                    </Alert>
                </Box>
                <Box>
                    <FormControl component="fieldset" variant="standard">
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled
                                        checked={orderSection.patient}
                                        onChange={handleSectionChange}
                                        name="patient"
                                        data-test="copy-order-dialog.section.patient"
                                    />
                                }
                                label={translate('orders:PATIENT')}
                            />
                            {!(isDentist && isCreatedByLab) ? (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            disabled
                                            checked={orderSection.construction}
                                            onChange={handleSectionChange}
                                            name="construction"
                                            data-test="copy-order-dialog.section.construction"
                                        />
                                    }
                                    label={translate('orders:CONSTRUCTION_DETAILS')}
                                />
                            ) : undefined}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={orderSection.comments}
                                        onChange={handleSectionChange}
                                        name="comments"
                                        data-test="copy-order-dialog.section.comments"
                                    />
                                }
                                label={translate('orders:COMMENTS')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={orderSection.files}
                                        onChange={handleSectionChange}
                                        name="files"
                                        data-test="copy-order-dialog.section.files"
                                    />
                                }
                                label={translate('orders:ATTACHED_FILES_AND_IMAGES')}
                            />
                        </FormGroup>
                    </FormControl>
                </Box>
            </Box>
        </DHDialog>
    );
};

export default CreateWarrantyOrderDialog;
